import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Copy, Check } from "lucide-react";
import { toast } from "sonner";

function CopyBlock({ code, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative">
      <pre className="rounded-xl bg-[#0F1F30] text-[#EAF1F7] text-xs p-4 overflow-x-auto font-mono">{code}</pre>
      <button
        className="absolute top-2 right-2 flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-white/10 hover:bg-white/20 text-white"
        onClick={() => { navigator.clipboard.writeText(code); setCopied(true); toast.success("Copié !"); setTimeout(() => setCopied(false), 1500); }}
        data-testid={testid}
      >
        {copied ? <Check className="w-3.5 h-3.5"/> : <Copy className="w-3.5 h-3.5"/>} {copied ? "Copié" : "Copier"}
      </button>
    </div>
  );
}

export default function Branding() {
  const { tenant, setTenant } = useAuth();
  const [b, setB] = useState(null);

  useEffect(() => {
    if (tenant) setB({
      business_name: tenant.business_name, logo_url: tenant.logo_url || "",
      primary_color: tenant.primary_color, accent_color: tenant.accent_color,
      phone: tenant.phone || "", email: tenant.email || "", website: tenant.website || "",
      cta_label: tenant.cta_label || "Obtenir mon devis",
      avg_call_duration_min: tenant.avg_call_duration_min || 3,
    });
  }, [tenant]);

  if (!b) return null;

  async function save() {
    const r = await api.put("/tenant/branding", b);
    setTenant(r.data);
    toast.success("Apparence enregistrée");
  }

  const publicUrl = `${window.location.origin}/quote/${tenant.slug}`;
  const iframe = `<iframe src="${publicUrl}" style="width:100%;height:820px;border:0;border-radius:16px" allow="fullscreen"></iframe>`;
  const button = `<a href="${publicUrl}" target="_blank" style="display:inline-block;background:${b.primary_color};color:#fff;padding:14px 28px;border-radius:9999px;font-family:sans-serif;font-weight:600;text-decoration:none">${b.cta_label}</a>`;

  return (
    <div>
      <h1 className="font-display text-3xl font-extrabold text-[#16324F]">Apparence & intégration</h1>
      <p className="mt-1 text-slate-500">Personnalisez l'expérience publique et intégrez MonTempsGagné sur votre site.</p>

      <section className="mt-8 grid md:grid-cols-2 gap-6">
        <div className="kf-card p-6">
          <h2 className="font-display font-bold text-lg text-[#16324F]">Identité</h2>
          <div className="mt-4 grid gap-4">
            <div className="grid gap-1.5"><Label>Nom de l'entreprise</Label><Input value={b.business_name} onChange={(e) => setB({...b, business_name: e.target.value})} data-testid="brand-name"/></div>
            <div className="grid gap-1.5"><Label>URL du logo (optionnel)</Label><Input value={b.logo_url} onChange={(e) => setB({...b, logo_url: e.target.value})} placeholder="https://..." data-testid="brand-logo"/></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5"><Label>Couleur principale</Label><div className="flex gap-2"><Input type="color" value={b.primary_color} onChange={(e) => setB({...b, primary_color: e.target.value})} className="h-10 w-14 p-1"/><Input value={b.primary_color} onChange={(e) => setB({...b, primary_color: e.target.value})}/></div></div>
              <div className="grid gap-1.5"><Label>Couleur d'accent</Label><div className="flex gap-2"><Input type="color" value={b.accent_color} onChange={(e) => setB({...b, accent_color: e.target.value})} className="h-10 w-14 p-1"/><Input value={b.accent_color} onChange={(e) => setB({...b, accent_color: e.target.value})}/></div></div>
            </div>
            <div className="grid gap-1.5"><Label>Libellé du bouton CTA</Label><Input value={b.cta_label} onChange={(e) => setB({...b, cta_label: e.target.value})} data-testid="brand-cta"/></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5"><Label>Téléphone</Label><Input value={b.phone} onChange={(e) => setB({...b, phone: e.target.value})}/></div>
              <div className="grid gap-1.5"><Label>Email</Label><Input value={b.email} onChange={(e) => setB({...b, email: e.target.value})}/></div>
            </div>
            <div className="grid gap-1.5"><Label>Site web</Label><Input value={b.website} onChange={(e) => setB({...b, website: e.target.value})} placeholder="https://..."/></div>
          </div>
          <Button onClick={save} className="mt-6 rounded-full bg-[#16324F] hover:bg-[#0F2438]" data-testid="save-branding"><Save className="w-4 h-4 mr-1"/>Enregistrer</Button>
        </div>

        <div className="kf-card p-6">
          <h2 className="font-display font-bold text-lg text-[#16324F]">Aperçu</h2>
          <div className="mt-4 rounded-2xl overflow-hidden border border-slate-200">
            <div style={{ background: b.primary_color, color: "white" }} className="p-8">
              <div className="text-xs font-bold uppercase tracking-widest" style={{color: b.accent_color}}>Serrurier automobile</div>
              <h3 className="mt-2 font-display text-2xl font-extrabold">{b.business_name}</h3>
              <a href={publicUrl} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center rounded-full font-semibold px-6 py-2.5 text-sm" style={{ background: b.accent_color, color: b.primary_color }}>{b.cta_label}</a>
            </div>
          </div>
          <div className="mt-4 text-xs text-slate-500">URL publique : <a className="text-[#19C3B3] font-mono" href={publicUrl} target="_blank" rel="noreferrer">{publicUrl}</a></div>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-display font-bold text-xl text-[#16324F]">Intégration sur votre site</h2>
        <p className="text-sm text-slate-500 mt-1">Trois façons d'ajouter MonTempsGagné à votre site existant.</p>

        <div className="mt-6 grid md:grid-cols-3 gap-6">
          <div className="kf-card p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-[#19C3B3]">Option A</div>
            <h3 className="font-display font-bold text-lg text-[#16324F] mt-1">Lien direct</h3>
            <p className="text-sm text-slate-500 mt-2">Ajoutez ce lien à votre navigation.</p>
            <div className="mt-4"><CopyBlock code={publicUrl} testid="copy-link"/></div>
          </div>
          <div className="kf-card p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-[#19C3B3]">Option B</div>
            <h3 className="font-display font-bold text-lg text-[#16324F] mt-1">Bouton</h3>
            <p className="text-sm text-slate-500 mt-2">Un bouton stylé à coller dans votre HTML.</p>
            <div className="mt-4"><CopyBlock code={button} testid="copy-button"/></div>
          </div>
          <div className="kf-card p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-[#19C3B3]">Option C</div>
            <h3 className="font-display font-bold text-lg text-[#16324F] mt-1">Widget intégré</h3>
            <p className="text-sm text-slate-500 mt-2">Un iframe responsive.</p>
            <div className="mt-4"><CopyBlock code={iframe} testid="copy-iframe"/></div>
          </div>
        </div>
      </section>
    </div>
  );
}
